import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate, useParams } from "react-router-dom";
import Header3 from "./Header3";
import BottomNav from "./BottomNav";
import ImageModal from "./ImageModal";
import EmptyState from "./EmptyState";
import { getImageSrc, handleImgError } from "./CarCard";
import { PageShell, PageContent, Section } from "./Page";
import { isLoggedIn } from "../session";

const VerPlace = () => {
  const { carId } = useParams();
  const [carData, setCarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [authUserId, setAuthUserId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAuthUserId = async () => {
      const user = await isLoggedIn();
      if (user) {
        setAuthUserId(user.uid);
      } else {
        navigate("/login");
      }
    };
    fetchAuthUserId();
  }, [navigate]);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const carDocRef = doc(db, "cars", carId);
        const carDoc = await getDoc(carDocRef);

        if (carDoc.exists()) {
          setCarData({ id: carDoc.id, ...carDoc.data() });
        }
      } catch {
        // Error ignorado intencionalmente
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [carId]);

  const formatDate = (timestamp) => {
    if (timestamp && timestamp.toDate instanceof Function) {
      return timestamp.toDate().toLocaleDateString();
    }
    return "";
  };

  const handleOpenImageModal = () => {
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const handleChatClick = async () => {
    if (!authUserId || !carData) return;

    try {
      const chatsRef = collection(db, "chats");
      const chatDoc = await addDoc(chatsRef, {
        carId: carData.id,
        participants: [authUserId, carData.uid],
        lastMessage: "",
        lastMessageTime: serverTimestamp(),
        unreadCount: {
          [authUserId]: 0,
          [carData.uid]: 0
        }
      });

      navigate(`/chatbox/${chatDoc.id}/${carData.id}`);
    } catch {
      // Error ignorado intencionalmente
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!carData) {
    return (
      <PageShell>
        <Header3 />
        <PageContent>
          <EmptyState title="No se encontró el vehículo" text="Puede que la publicación haya sido eliminada." />
        </PageContent>
        <BottomNav />
      </PageShell>
    );
  }

  const { title, description, price, city, direccion, modelo, km, marca, createdAt, images, uid } = carData;
  const isOwner = authUserId === uid;

  const specs = [
    { label: "Marca", value: marca },
    { label: "Modelo", value: modelo },
    { label: "Kilómetros", value: km ? `${km} km` : "" },
    { label: "Ciudad", value: city },
  ];

  return (
    <PageShell>
      <Header3 subtitle={[marca, modelo].filter(Boolean).join(" · ")} />
      <PageContent>
        <Grid container spacing={3}>
          {/* Galería */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{ position: 'relative', borderRadius: 4, overflow: 'hidden', cursor: 'zoom-in', bgcolor: 'background.paper' }}
              onClick={handleOpenImageModal}
            >
              <Box
                component="img"
                src={getImageSrc(images?.[selectedImage])}
                alt={title}
                onError={handleImgError}
                sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(17, 15, 36, 0.7)',
                  color: '#fff',
                  fontSize: 13,
                }}
              >
                <ZoomInIcon sx={{ fontSize: 18 }} />
                {images?.length || 0} fotos
              </Box>
            </Box>
            {images && images.length > 1 && (
              <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', mt: 1.5, pb: 0.5 }}>
                {images.map((img, index) => (
                  <Box
                    key={index}
                    component="img"
                    src={getImageSrc(img)}
                    alt={`${title} miniatura ${index + 1}`}
                    onError={handleImgError}
                    sx={{
                      width: 84,
                      height: 63,
                      flexShrink: 0,
                      objectFit: 'cover',
                      borderRadius: 2,
                      cursor: 'pointer',
                      outline: '2px solid',
                      outlineOffset: 2,
                      outlineColor: selectedImage === index ? 'secondary.main' : 'transparent',
                      opacity: selectedImage === index ? 1 : 0.7,
                      transition: 'opacity .2s',
                      '&:hover': { opacity: 1 },
                    }}
                    onClick={() => setSelectedImage(index)}
                  />
                ))}
              </Stack>
            )}

            {description && (
              <Section title="Descripción" sx={{ mt: 3 }}>
                <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                  {description}
                </Typography>
              </Section>
            )}
          </Grid>

          {/* Detalle */}
          <Grid item xs={12} md={5}>
            <Section sx={{ position: { md: 'sticky' }, top: { md: 96 } }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h5" component="h2">
                    {title}
                  </Typography>
                  <Typography variant="h4" sx={{ color: 'secondary.dark', mt: 1 }}>
                    $ {price}
                  </Typography>
                </Box>

                <Grid container spacing={1.5}>
                  {specs.map((spec) => (
                    <Grid item xs={6} key={spec.label}>
                      <Box sx={{ bgcolor: 'background.default', borderRadius: 2.5, px: 1.5, py: 1.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          {spec.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                          {spec.value || "—"}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {direccion && (
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <PlaceOutlinedIcon sx={{ color: 'secondary.dark', fontSize: 20, mt: 0.25 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Ubicación
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {direccion}
                      </Typography>
                    </Box>
                  </Stack>
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  fullWidth
                  startIcon={!isOwner && <ChatBubbleOutlineIcon />}
                  onClick={handleChatClick}
                  disabled={isOwner}
                >
                  {isOwner ? "Este es tu anuncio" : "Consultar al vendedor"}
                </Button>

                <Divider />
                <Typography variant="caption" color="text.secondary">
                  Publicado el {formatDate(createdAt)}
                </Typography>
              </Stack>
            </Section>
          </Grid>
        </Grid>
      </PageContent>

      <ImageModal
        open={openImageModal}
        onClose={handleCloseImageModal}
        images={images}
      />
      <BottomNav />
    </PageShell>
  );
};

export default VerPlace;
